import { getFillColor } from "./spiderweb-utils";

import type { FillColor } from "./spiderweb-types";

const ROVO_QUADRANT_COLORS: readonly [
    FillColor,
    FillColor,
    FillColor,
    FillColor
] = ["lime500", "yellow400", "purple500", "blue500"];

function safeNodeIdForSvg(nodeId: string): string {
    return nodeId.replace(/[^\w.-]/g, "_");
}

export type RovoRainbowBorderShape = "pill" | "rectangle";

type RovoRainbowBorderProps = {
    nodeId: string;
    shape: RovoRainbowBorderShape;
    innerWidth: number;
    innerHeight: number;
    innerRectangleBorderRadius?: number;
    ringWidth: number;
};

export const RovoRainbowBorder = ({
    nodeId,
    shape,
    innerWidth,
    innerHeight,
    innerRectangleBorderRadius,
    ringWidth
}: RovoRainbowBorderProps) => {
    const B = ringWidth;
    const W_out = innerWidth + 2 * B;
    const H_out = innerHeight + 2 * B;
    const x0 = -W_out / 2;
    const y0 = -H_out / 2;
    const halfW = W_out / 2;
    const halfH = H_out / 2;
    const rxOut =
        shape === "pill"
            ? H_out / 2
            : innerRectangleBorderRadius !== undefined
            ? Math.min(
                  innerRectangleBorderRadius + B,
                  Math.min(W_out, H_out) / 2
              )
            : Math.min(W_out, H_out) / 4;

    const sid = safeNodeIdForSvg(nodeId);
    const clipId = (q: "tl" | "tr" | "bl" | "br") => `rr-${sid}-${q}`;

    const clips = (
        <defs>
            <clipPath id={clipId("tl")}>
                <rect x={x0} y={y0} width={halfW} height={halfH} />
            </clipPath>
            <clipPath id={clipId("tr")}>
                <rect x={0} y={y0} width={halfW} height={halfH} />
            </clipPath>
            <clipPath id={clipId("bl")}>
                <rect x={x0} y={0} width={halfW} height={halfH} />
            </clipPath>
            <clipPath id={clipId("br")}>
                <rect x={0} y={0} width={halfW} height={halfH} />
            </clipPath>
        </defs>
    );

    const outerRectAttrs = {
        x: x0,
        y: y0,
        width: W_out,
        height: H_out,
        rx: rxOut
    };

    const quadrants = (["tl", "tr", "bl", "br"] as const).map((q, i) => (
        <g key={q} clipPath={`url(#${clipId(q)})`}>
            <rect
                {...outerRectAttrs}
                fill={getFillColor(
                    (ROVO_QUADRANT_COLORS[i] as FillColor) || "lime500"
                )}
            />
        </g>
    ));

    return (
        <>
            {clips}
            <g
                className="spiderweb-node__rovo-rainbow-border"
                pointerEvents="none"
            >
                {quadrants}
            </g>
        </>
    );
};
